import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { ODataParams, odataParamsFactory, ODataResponse } from '@tdw/core';
import { catchError, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { EnvironmentConfig, ENV_CONFIG } from '../../environments/environment.model';
import { FittingAssociatedProduct, FittingProduct, FittingProductAssociatedProduct, fittingProductFactory, FlatterFilesResponse } from '../models';
import { ProductConfiguration, FittingDetail, FittingQueryParams, Inventtxt, productConfigurationFactory } from '../models/fitting-query-params.model';
import { AppLookup, ConfigurationFiltersLookups, FittingLookups, fittingLookupsFactory, FittingQueryResult, LookupValue } from '../models/lookup.model';
import { FittingProductFormAssociationComponent } from '../modules/admin/fitting-product-form-association/fitting-product-form-association.component';
import { Logger } from '../util/logger';



@Injectable({
  providedIn: 'root'
})
export class FittingsService {
  public lookupKey = 'prodSelector_fittingLookups';

  constructor(private http: HttpClient, private logger: Logger,
    @Inject(ENV_CONFIG) private config: EnvironmentConfig) { }

  private getRegions(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' })
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/Hemispheres${q.toQuery()}`).pipe(map(res => res.value || []));
  }
  private getDesignFactors(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' })
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/FittingDesignFactors${q.toQuery()}`).pipe(map(res => res.value || []));
  }
  private getPipeSizes(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' })
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/FittingPipeSizes${q.toQuery()}`).pipe(map(res => res.value || []));
  }
  private getAnsiClasses(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' })
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/FittingClasses${q.toQuery()}`).pipe(map(res => res.value || []));
  }
  private getFlangeTypes(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' })
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/FittingFlangeTypes${q.toQuery()}`).pipe(map(res => res.value || []));
  }
  private getDesignCodes(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' })
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/FittingDesignCodes${q.toQuery()}`).pipe(map(res => res.value || []));
  }
  private getMaxCes(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' })
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/FittingMaxCes${q.toQuery()}`).pipe(map(res => res.value || []));
  }
  private getFittingTypes(): Observable<LookupValue[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ orderBy: 'SortOrder' })
    return this.http.get<ODataResponse<LookupValue[]>>(`${endpoint?.url}odata/${endpoint?.version}/FittingTypes${q.toQuery()}`).pipe(map(res => res.value || []));
  }

  getLookups(force: boolean): Observable<FittingLookups> {
    const cachedLookups = JSON.parse(localStorage.getItem(this.lookupKey) || '{}');
    const expired = !cachedLookups.expiration ? true : new Date().getTime() > new Date(cachedLookups.expiration).getTime();
    if (!force && !!cachedLookups && !expired) {
      this.logger.log('Fitting Lookups from cache')
      return of(cachedLookups);
    }

    return forkJoin([
      this.getRegions(),
      this.getDesignFactors(),
      this.getPipeSizes(),
      this.getAnsiClasses(),
      this.getFlangeTypes(),
      this.getDesignCodes(),
      this.getMaxCes(),
      this.getFittingTypes()])
      .pipe(
        tap(([regions, designFactors, pipeSizes, ansiClasses, flangeTypes, designCodes, maxCes, fittingTypes]) => {
          const obj = fittingLookupsFactory({ regions, designFactors, pipeSizes, ansiClasses, flangeTypes, designCodes, maxCes, fittingTypes });
          localStorage.setItem(this.lookupKey, JSON.stringify({ ...obj, expiration: new Date(new Date().getTime() + (60 * 60 * 24 * 1000)) }));
        }),
        map(([regions, designFactors, pipeSizes, ansiClasses, flangeTypes, designCodes, maxCes, fittingTypes]) =>
          fittingLookupsFactory({
            regions, designFactors, pipeSizes, ansiClasses, flangeTypes, designCodes, maxCes, fittingTypes
          })),
      );
  }

  getFittingDrawing(id: number): Observable<Blob> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ArrayBuffer>(`${endpoint?.url}api/${endpoint?.version}/Drawings/fitting/${id}`,
      { responseType: 'arraybuffer' as 'json' })
      .pipe(map(res => new Blob([new Uint8Array(res, 0, res.byteLength)], { type: 'application/pdf' })));
  }

  getFittingAssociatedDrawing(id: number): Observable<Blob> {
    if (!id) return of();
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ArrayBuffer>(`${endpoint?.url}api/${endpoint?.version}/Drawings/fittingAssociatedProduct/${id}`,
      { responseType: 'arraybuffer' as 'json' })
      .pipe(map(res => new Blob([new Uint8Array(res, 0, res.byteLength)], { type: 'application/pdf' })));
  }

  getFlatterFilesDrawingByPartNumber(partNumber: string): Observable<Blob> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ArrayBuffer>(`${endpoint?.url}api/${endpoint?.version}/Drawings/flatterfiles/${partNumber}`,
      { responseType: 'arraybuffer' as 'json' })
      .pipe(map(res => new Blob([new Uint8Array(res, 0, res.byteLength)], { type: 'application/pdf' })));
  }

  getFlatterFilesDrawingDictionary(partNumber: string): Observable<{ [key: string]: FlatterFilesResponse }> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<{ [key: string]: FlatterFilesResponse }>(`${endpoint?.url}api/${endpoint?.version}/Drawings/flatterfiles/forPartNumber/${partNumber}`)
  }

  getQueryResult(query: FittingQueryParams | undefined): Observable<FittingQueryResult[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const region = !!query?.hemisphereId ? `hemisphereId=${query?.hemisphereId}` : null;
    // pipeline type doesn't matter for the query, it only filters down the pipe sizes
    const pipeSize = (query?.pipeSizeId || -1) > -1 ? `pipeSizeId=${query?.pipeSizeId}` : null;
    const fittingType = (query?.fittingTypeId || -1) > -1 ? `fittingTypeId=${query?.fittingTypeId}` : null;
    const ansiClass = (query?.classId || -1) > -1 ? `classId=${query?.classId}` : null;
    const flangeType = (query?.flangeTypeId || -1) > -1 ? `flangeTypeId=${query?.flangeTypeId}` : null;
    const branchSize = (query?.branchSizeId || -1) > -1 ? `branchSizeId=${query?.branchSizeId}` : null;
    const designCode = (query?.designCodeId || -1) > -1 ? `designCodeId=${query?.designCodeId}` : null;
    const designFactor = (query?.designFactorId || -1) > -1 ? `designFactorId=${query?.designFactorId}` : null;
    const maxPressure = (query?.maxDesignPressure || -1) > -1 ? `maxDesignPressure=${query?.maxDesignPressure}` : null;
    const designTempMin = !!query?.designTempMin && (query?.designTempMin || -1) > -1 ? `minDesignTemp=${query?.designTempMin}` : null;
    const designTempMax = !!query?.designTempMax && (query?.designTempMax || -1) > -1 ? `maxDesignTemp=${query?.designTempMax}` : null;
    const maxCe = (query?.maxCeId || -1) > -1 ? `maxCeId=${query?.maxCeId}` : null;

    const arr: any[] = [0, 1]; // we have to check for exists of a 1 or 0 because we want to have a negative (0) filter
    const isBA = (arr.includes(query?.isBuyAmerica)) ? `isBuyAmerica=${query!.isBuyAmerica}` : null;

    const limit125 = !!query && ((query.limit125 || 0) > -1) ? `limit125=${query?.limit125}` : null;

    const q = [region, pipeSize, fittingType, ansiClass, flangeType, branchSize, designCode, designFactor, maxPressure, designTempMin, designTempMax, maxCe, limit125].filter(f => !!f);
    const qStr = isBA == null ? q.join('&') : q.join('&').concat('&', isBA);
    return this.http.get<FittingQueryResult[]>(`${endpoint?.url}api/${endpoint?.version}/FittingProducts?${qStr}`);
  }

  private getCommercialDescriptions(itemId: string): Observable<Inventtxt[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ filter: `StartsWith(ItemId,'${itemId}')` })
    return this.http.get<ODataResponse<Inventtxt[]>>(`${endpoint?.url}odata/${endpoint?.version}/Inventtxts${q.toQuery()}`)
      .pipe(map(m => m.value || []));
  }

  // change this to use the partNumber instead of the id
  private getProductDetails(partNumber?: string) {
    // if (id === -1) return of(undefined);
    if (!partNumber) return of(undefined);
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<FittingDetail>(`${endpoint?.url}api/v2/FittingProducts/${partNumber}`);
  }

  private getConfigurations(partNumber?: string): Observable<ProductConfiguration[]> {
    if (!partNumber) return of([]);
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({ filter: `ProductNumber eq '${partNumber}' AND IsActive eq true`, expand: 'availableConfiguration($expand=AvailableConfigurationFilters)' });
    const configurations$ = this.http.get<ODataResponse<ProductConfiguration[]>>(`${endpoint?.url}odata/${endpoint?.version}/ProductConfigurations${q.toQuery()}`)
      .pipe(map(c => c.value || []));
    const leadTimesWithSurge$ = this.getLeadTimesAndSurge(partNumber);

    return forkJoin([configurations$, leadTimesWithSurge$])
      .pipe(
        map(([configurations, leadTimesWithSurge]) => {
          return configurations.map(config => ({
            ...config,
            leadTime: leadTimesWithSurge.find(f => f.itemId === `${config.productNumber}${config.productLine}${config.suffix}`)?.leadtime,
            surge: leadTimesWithSurge.find(f => f.itemId === `${config.productNumber}${config.productLine}${config.suffix}`)?.surge
          }));
        }));
  }

  getLeadTimesAndSurge(partNumber: string): Observable<{ itemId: string, leadtime: number, surge: number }[]> {
    const endpoint = this.config.endpoints?.baseApi;
    const leadTimesQ = odataParamsFactory({ filter: `Inventdimid eq 'DN00000181' AND DATAAREAID eq 'MDCG' AND startsWith(ITEMID, '${partNumber}')`, select: ['ITEMID', 'LEADTIME', 'HIGHESTQTY'] });
    return this.http.get<ODataResponse<{ itemid: string, leadtime: number, highestqty: number }[]>>(`${endpoint?.url}odata/${endpoint?.version}/Inventitemsalessetups${leadTimesQ.toQuery()}`)
      .pipe(map(m => (m.value || []).map(res => ({ itemId: res.itemid, leadtime: res.leadtime, surge: res.highestqty }))));
  }

  getDetail(id: number, partNumber?: string): Observable<{
    fittingDetail?: FittingDetail,
    configurations: ProductConfiguration[],
    commercialDescriptions: Inventtxt[]
  } | undefined> {
    if (id === -1) return of(undefined);
    return forkJoin([
      this.getProductDetails(partNumber),
      this.getConfigurations(partNumber),
      this.getCommercialDescriptions(partNumber || '-foo')])
      .pipe(map(([fittingDetail, configurations, commercialDescriptions]) => ({
        fittingDetail,
        configurations: configurations.map(config => ({
          ...config,
          commercialDescription: this.getDescription(config, commercialDescriptions, fittingDetail)
        })),
        commercialDescriptions
      })));
  }
  getDescription(config: ProductConfiguration, commercialDescriptions: Inventtxt[], fittingDetail: FittingDetail | undefined): string {
    const comm = commercialDescriptions.find(desc => desc.itemid.toUpperCase() === `${config.productNumber?.toUpperCase()}${config.productLine?.toUpperCase()}${config.suffix?.toUpperCase()}`);
    return !comm ? fittingDetail?.productName || '' : comm.txt;
  }

  getFittingMaintenance(q: ODataParams): Observable<ODataResponse<FittingProduct[]>> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ODataResponse<FittingProduct[]>>(`${endpoint?.url}odata/${endpoint?.version}/FittingProducts${q.toQuery()}`);
  }

  getFittingById(id: number): Observable<FittingProduct | null> {
    if (id === -1) return of(fittingProductFactory({ id: undefined }));
    const endpoint = this.config.endpoints?.baseApi;
    const q = odataParamsFactory({
      filter: `id eq ${id}`,
      expand: 'FittingType,Hemisphere,FittingPipeSize,FittingClass,FittingDesignFactor,FittingBranchSize,FittingFlangeType,FittingMaxCe,FittingProductAssociatedProducts,FittingProductDesignCodes($expand=FittingDesignCode),FittingProductDesignPressures($expand=FittingDesignFactor)',
      select: ['id', 'partNumber', 'productName', 'description', 'pipeSizeId', 'classId', 'designFactorId', 'branchSizeId', 'flangeTypeId', 'maxDesignPressure', 'minDesignTemp', 'maxDesignTemp', 'maxCeid', 'limit125', 'notes', 'restricted', 'restrictedNotice', 'inactiveDate', 'createdDate', 'createdBy', 'updatedDate', 'updatedBy', 'fittingTypeId', 'hemisphereId', 'isPreferred'],
    })

    return this.http.get<ODataResponse<FittingProduct[]>>(`${endpoint?.url}odata/${endpoint?.version}/FittingProducts${q.toQuery()}`)
      .pipe(map(v => (v?.value || []).length > 0 ? (v?.value || [])[0] : null));
  }

  getFittingAssociatedProducts(q: ODataParams): Observable<FittingAssociatedProduct[]> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ODataResponse<FittingAssociatedProduct[]>>(`${endpoint?.url}odata/${endpoint?.version}/FittingAssociatedProducts${q.toQuery()}`)
      .pipe(map(v => v.value || []));
  }

  private createFitting(fitting: FittingProduct): Observable<any> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.post<any>(`${endpoint?.url}odata/${endpoint?.version}/FittingProducts`, fitting);
  }

  saveFitting(fitting: FittingProduct): Observable<any> {
    if (!fitting.id) return this.createFitting(fitting);

    const endpoint = this.config.endpoints?.baseApi;
    return this.http.patch<any>(`${endpoint?.url}odata/${endpoint?.version}/FittingProducts(${fitting.id})`, fitting);
  }

  addDesignCode(obj: { fittingProductId: number, designCodeId: number }): Observable<FittingDetail> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.post<any>(`${endpoint?.url}odata/${endpoint?.version}/FittingProductDesignCodes`, obj);
  }

  removeDesignCode(fittingProductDesignCodeId: number): Observable<any> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.delete<any>(`${endpoint?.url}odata/${endpoint?.version}/FittingProductDesignCodes(${fittingProductDesignCodeId})`);
  }

  addDesignPressure(obj: { maxDesignPressure: number, designFactorId: number, fittingProductId: number }) {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.post<any>(`${endpoint?.url}odata/${endpoint?.version}/FittingProductDesignPressures`, obj);
  }

  removeDesignPressure(fittingProductDesignPressureId: number): Observable<any> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.delete<any>(`${endpoint?.url}odata/${endpoint?.version}/FittingProductDesignPressures(${fittingProductDesignPressureId})`);
  }

  queryFittingProductAssociatedProducts(q: ODataParams): Observable<ODataResponse<FittingProductAssociatedProduct[]>> {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ODataResponse<FittingProductAssociatedProduct[]>>(`${endpoint?.url}odata/${endpoint?.version}/FittingProductAssociatedProducts${q.toQuery()}`);
  }

  addAssociatedProduct(obj: { fittingProductId: number, associatedProductId: number }) {
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.post<any>(`${endpoint?.url}odata/${endpoint?.version}/FittingProductAssociatedProducts`, obj);
  }

  removeAssociatedProduct(associatedProductId: number, fittingId: number): Observable<any> {
    const endpoint = this.config.endpoints?.baseApi;
    const fittingProductAssociatedProduct$ = this.queryFittingProductAssociatedProducts(odataParamsFactory({
      filter: `fittingProductId eq ${fittingId} AND AssociatedProductId eq ${associatedProductId}`
    })).pipe(map(m => m.value?.at(0)));

    return fittingProductAssociatedProduct$.pipe(switchMap(item => this.http.delete(`${endpoint?.url}odata/${endpoint?.version}/FittingProductAssociatedProducts(${item?.id || -1})`)))
  }

  loadConfigurationLookups(tappedHoles: AppLookup[], orings: AppLookup[], bevels: AppLookup[]): Observable<ConfigurationFiltersLookups> {
    return forkJoin([this.loadBevelConfigurationOptions(bevels), this.loadOringConfigurationOptions(orings), this.loadTappedHoleSizeConfigurationOptions(tappedHoles)])
      .pipe(map(([bevelOptions, oringOptions, tappedHoleSizes]) => ({ bevelOptions, oringOptions, tappedHoleSizes })));
  }

  private loadBevelConfigurationOptions(val: AppLookup[]): Observable<AppLookup[]> {
    if (val.length > 0) return of(val);
    const date = new Date().toISOString();
    const q = odataParamsFactory({ filter: `lookupType eq 'BevelOptions' AND (inactiveDate lt ${date} OR inactiveDate eq null)` });
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ODataResponse<AppLookup[]>>(`${endpoint?.url}odata/${endpoint?.version}/AppLookups${q.toQuery()}`).pipe(map(res => res.value || []));
  }

  private loadOringConfigurationOptions(val: AppLookup[]): Observable<AppLookup[]> {
    if (val.length > 0) return of(val);
    const date = new Date().toISOString();
    const q = odataParamsFactory({ filter: `lookupType eq 'OringOptions' AND (inactiveDate lt ${date} OR inactiveDate eq null)` });
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ODataResponse<AppLookup[]>>(`${endpoint?.url}odata/${endpoint?.version}/AppLookups${q.toQuery()}`).pipe(map(v => v.value || []));
  }

  private loadTappedHoleSizeConfigurationOptions(val: AppLookup[]): Observable<AppLookup[]> {
    if (val.length > 0) return of(val);
    const date = new Date().toISOString();
    const q = odataParamsFactory({ filter: `lookupType eq 'TappedHoleSize' AND (inactiveDate lt ${date} OR inactiveDate eq null)` });
    const endpoint = this.config.endpoints?.baseApi;
    return this.http.get<ODataResponse<AppLookup[]>>(`${endpoint?.url}odata/${endpoint?.version}/AppLookups${q.toQuery()}`).pipe(map(v => v.value || []));
  }
}
